'use client';

import { FormEvent, useState } from 'react';

import { useContactDialog } from '../components/contact/contact-dialog-provider.component';
import {
  buildContactTopicMailtoHref,
  getContactTopic,
} from '../../utils/contact-topics.util';

/**
 * Inline subscribe box. The address typed here is handed straight to the shared
 * contact dialog, where the visitor completes the bot check and the worker
 * sends the notification email — the same path every other CTA on the site
 * takes. Without the dialog (no provider, no scripting) it degrades to the
 * original `mailto:`.
 */
export function BlogNewsletterCta() {
  const [subscriberEmail, setSubscriberEmail] = useState('');
  const contactDialog = useContactDialog();

  function handleNewsletterSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const trimmedEmail = subscriberEmail.trim();
    if (!trimmedEmail) {
      return;
    }

    if (contactDialog) {
      contactDialog.openContactDialog('newsletter-subscribe', {
        initialEmail: trimmedEmail,
      });
      return;
    }

    window.location.href = buildContactTopicMailtoHref(
      getContactTopic('newsletter-subscribe'),
    );
  }

  return (
    <section id="blog-newsletter" className="mb-8 scroll-mt-20 py-8">
      <div className="relative overflow-hidden rounded-xl bg-[#2d3133] p-8 text-center sm:p-12">
        <div className="pointer-events-none absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,#006b2c,transparent)]" />
        </div>
        <div className="relative z-10 mx-auto max-w-2xl space-y-4">
          <h2 className="text-2xl font-bold text-white sm:text-3xl">
            Get the latest from our blog
          </h2>
          <p className="text-slate-300">
            Join engineers who receive our bi-weekly deep dives into software
            architecture, cloud scaling, and team leadership.
          </p>
          <form
            className="flex flex-col gap-2 pt-2 sm:flex-row sm:items-stretch"
            onSubmit={handleNewsletterSubmit}
          >
            <input
              type="email"
              name="email"
              autoComplete="email"
              required
              value={subscriberEmail}
              onChange={(event) => setSubscriberEmail(event.target.value)}
              placeholder="Enter your email"
              className="min-w-0 w-full flex-1 rounded-lg border-none bg-white px-6 py-3 text-gray-900 placeholder:text-slate-500 focus:ring-2 focus:ring-primary focus:outline-none"
              aria-label="Email address"
            />
            <button
              type="submit"
              className="shrink-0 whitespace-nowrap rounded-lg bg-primary px-8 py-3 text-sm font-bold text-primary-foreground transition-all hover:opacity-90 active:scale-95"
            >
              Subscribe
            </button>
          </form>
          <p className="text-xs text-slate-400 opacity-70">
            No spam. Only high-quality technical content. Unsubscribe anytime.
          </p>
        </div>
      </div>
    </section>
  );
}
